import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';
import { useOrganization } from './OrganizationContext';
import { useClients } from './ClientsContext';
import { Project, TimeEntry, Material, ProjectAttachment } from '../types';

interface ProjectsContextType {
  projects: Project[];
  addProject: (project: Omit<Project, 'id'>) => Promise<Project>;
  updateProject: (project: Project) => void;
  deleteProject: (id: string) => void;
  getProjectById: (id: string) => Project | undefined;
  addTimeEntry: (projectId: string, timeEntry: Omit<TimeEntry, 'id'>) => void;
  updateTimeEntry: (projectId: string, timeEntry: TimeEntry) => void;
  deleteTimeEntry: (projectId: string, timeEntryId: string) => void;
  addMaterialToProject: (projectId: string, material: Material, updateInventory?: (id: string, quantity: number) => void) => void;
  removeMaterialFromProject: (projectId: string, materialId: string, updateInventory?: (id: string, quantity: number) => void) => void;
  updateMaterialInProject: (projectId: string, material: Material) => void;
  addAttachmentToProject: (projectId: string, attachment: Omit<ProjectAttachment, 'id' | 'uploadedAt'>) => void;
  removeAttachmentFromProject: (projectId: string, attachmentId: string) => void;
}

const ProjectsContext = createContext<ProjectsContextType | undefined>(undefined);

export const useProjects = () => {
  const context = useContext(ProjectsContext);
  if (!context) {
    throw new Error('useProjects must be used within a ProjectsProvider');
  }
  return context;
};

export const ProjectsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const { currentOrganization } = useOrganization();
  const { fetchClients } = useClients();
  const [projects, setProjects] = useState<Project[]>([]);

  // Fetch projects from Supabase when authenticated and org is selected
  useEffect(() => {
    if (isAuthenticated && currentOrganization) {
      fetchProjects();
    }
  }, [isAuthenticated, currentOrganization]);

  const fetchProjects = async () => {
    if (!currentOrganization) {
      console.log('No organization selected, skipping projects fetch');
      return;
    }

    try {
      console.log('📥 Fetching projects for organization:', currentOrganization.id);
      const { data, error } = await supabase
        .from('projects')
        .select(`
          *,
          client:clients(*)
        `)
        .eq('organization_id', currentOrganization.id)
        .order('created_at', { ascending: false });

      if (error) {
        console.error('❌ Error fetching projects:', error);
        return;
      }

      console.log('✅ Fetched projects:', data?.length || 0);
      if (data && data.length > 0) {
        console.log('📊 Sample project:', {
          id: data[0].id,
          title: data[0].title,
          client_id: data[0].client_id,
          has_client: !!data[0].client,
          client_name: data[0].client?.name || 'N/A'
        });
      }

      if (data) {
        // Transform Supabase data to Project format
        const transformedProjects: Project[] = await Promise.all(
          data.map(async (project) => {
            // Log if client is missing for debugging
            if (!project.client && project.client_id) {
              console.warn('⚠️ Project has client_id but client relationship is missing:', {
                project_id: project.id,
                project_title: project.title,
                client_id: project.client_id
              });
              
              // Try to manually fetch the client if JOIN failed
              try {
                const { data: clientData, error: clientError } = await supabase
                  .from('clients')
                  .select('*')
                  .eq('id', project.client_id)
                  .eq('organization_id', currentOrganization.id)
                  .maybeSingle();
                
                if (clientError) {
                  console.error('❌ Error fetching client manually:', clientError);
                } else if (clientData) {
                  console.log('✅ Manually fetched client:', clientData.name);
                  // Use the manually fetched client
                  return {
                    id: project.id,
                    title: project.title,
                    description: project.description || '',
                    category: project.category,
                    clientId: project.client_id, // Preserve clientId
                    client: {
                      name: clientData.name,
                      address: clientData.address || '',
                      phone: clientData.phone,
                      email: clientData.email
                    },
                    status: project.status,
                    createdAt: project.created_at,
                    scheduledDate: project.scheduled_date,
                    completedDate: project.completed_date,
                    timeEntries: project.time_entries || [],
                    materials: project.materials || [],
                    attachments: project.attachments || [],
                    hourlyRate: project.hourly_rate,
                    serviceId: project.service_id,
                    notes: project.notes,
                    assignedMembers: project.assigned_members || [],
                    projectManager: project.project_manager,
                    priority: project.priority,
                    estimatedHours: project.estimated_hours,
                    isFixedPrice: project.is_fixed_price,
                    fixedPrice: project.fixed_price
                  };
                }
              } catch (fetchError) {
                console.error('❌ Error in manual client fetch:', fetchError);
              }
            }
            
          return {
            id: project.id,
            title: project.title,
            description: project.description || '',
            category: project.category,
            clientId: project.client_id, // ✅ Preserve clientId
            client: {
              name: project.client?.name || 'Unknown Client',
              address: project.client?.address || '',
              phone: project.client?.phone,
              email: project.client?.email
            },
            status: project.status,
            createdAt: project.created_at,
            scheduledDate: project.scheduled_date,
            completedDate: project.completed_date,
            timeEntries: project.time_entries || [],
            materials: project.materials || [],
            attachments: project.attachments || [],
            hourlyRate: project.hourly_rate,
            serviceId: project.service_id,
            notes: project.notes,
            assignedMembers: project.assigned_members || [],
            projectManager: project.project_manager,
            priority: project.priority,
            estimatedHours: project.estimated_hours,
            isFixedPrice: project.is_fixed_price,
            fixedPrice: project.fixed_price
          };
        })
        );

        setProjects(transformedProjects);
      }
    } catch (error) {
      console.error('Error in fetchProjects:', error);
    }
  };

  const addProject = async (project: Omit<Project, 'id'>): Promise<Project> => {
    if (!currentOrganization) {
      console.error('Cannot create project: No organization selected');
      throw new Error('Cannot create project: No organization selected');
    }

    try {
      // First, find or create the client (if provided)
      let clientId: string | null = null;
      
      // Check if this is a placeholder/TBD client (when user skips client info)
      const isPlaceholderClient = project.client.name === 'TBD' || 
                                  project.client.address === 'To be determined';
      
      if (isPlaceholderClient) {
        // User skipped client info - Don't create any client
        console.log('📋 Creating project without client (skipped client info)');
        console.log('ℹ️ Client can be added later by editing the project');
        clientId = null; // No client for this project
      } else {
        // Regular client handling - User provided client information
        console.log('👤 Creating project with real client:', project.client.name);
        console.log('📋 Client details:', {
          name: project.client.name,
          email: project.client.email,
          address: project.client.address,
          phone: project.client.phone
        });
        
        // First try to find client by email if email is provided
        let existingClient = null;
        if (project.client.email) {
          console.log('🔍 Searching for client by email:', project.client.email);
          const { data, error: emailSearchError } = await supabase
            .from('clients')
            .select('id')
            .eq('organization_id', currentOrganization.id)
            .eq('email', project.client.email)
            .maybeSingle();
          
          if (emailSearchError) {
            console.error('⚠️ Error searching by email:', emailSearchError);
          } else if (data) {
            existingClient = data;
            console.log('✅ Found client by email:', data.id);
          }
        }
        
        // If no client found by email, try by name and address
        if (!existingClient) {
          console.log('🔍 Searching for client by name and address:', project.client.name, project.client.address);
          const { data, error: nameSearchError } = await supabase
            .from('clients')
            .select('id')
            .eq('organization_id', currentOrganization.id)
            .eq('name', project.client.name)
            .eq('address', project.client.address)
            .maybeSingle();
          
          if (nameSearchError) {
            console.error('⚠️ Error searching by name/address:', nameSearchError);
          } else if (data) {
            existingClient = data;
            console.log('✅ Found client by name/address:', data.id);
          }
        }

        if (existingClient) {
          clientId = existingClient.id;
          console.log('✅ Using existing client with ID:', clientId);
        } else {
          // Create new client
          console.log('➕ Creating new client in database...');
          const { data: newClient, error: clientError } = await supabase
            .from('clients')
            .insert({
              organization_id: currentOrganization.id,
              name: project.client.name,
              address: project.client.address,
              phone: project.client.phone || null,
              email: project.client.email || null
            })
            .select('id')
            .single();

          if (clientError) {
            console.error('❌ Error creating client:', clientError);
            throw clientError;
          }

          if (!newClient || !newClient.id) {
            console.error('❌ Client creation returned no ID');
            throw new Error('Failed to create client: No ID returned');
          }

          clientId = newClient.id;
          console.log('✅ Created new client with ID:', clientId);
          
          // Refresh clients list so the new client appears immediately
          try {
            await fetchClients();
            console.log('✅ Clients list refreshed');
          } catch (refreshError) {
            console.warn('⚠️ Could not refresh clients list:', refreshError);
            // Don't throw - client was created successfully, just refresh failed
          }
        }
      }
      
      console.log('📌 Final client_id for project:', clientId);

      // Create the project
      console.log('📝 Creating project with client_id:', clientId);
      const { data, error} = await supabase
        .from('projects')
        .insert({
          organization_id: currentOrganization?.id,
          title: project.title,
          description: project.description,
          category: project.category || null,
          client_id: clientId,
          status: project.status,
          scheduled_date: project.scheduledDate || null,
          completed_date: project.completedDate || null,
          hourly_rate: project.hourlyRate,
          service_id: project.serviceId || null, // UUID field - must be null if empty
          notes: project.notes,
          assigned_members: project.assignedMembers || [],
          project_manager: project.projectManager || null, // UUID field - must be null if empty
          priority: project.priority || 'medium',
          estimated_hours: project.estimatedHours || null,
          is_fixed_price: project.isFixedPrice || false,
          fixed_price: project.fixedPrice || null,
          time_entries: project.timeEntries || [],
          materials: project.materials || [],
          attachments: project.attachments || []
        })
        .select(`
          *,
          client:clients(*)
        `)
        .single();

      if (error) {
        console.error('❌ Error adding project:', error);
        console.error('❌ Project data attempted:', {
          title: project.title,
          client_id: clientId,
          organization_id: currentOrganization?.id
        });
        throw error;
      }

      if (data) {
        console.log('✅ Project created successfully');
        console.log('📊 Project data returned:', {
          id: data.id,
          title: data.title,
          client_id: data.client_id,
          client_relationship: data.client ? {
            id: data.client.id,
            name: data.client.name,
            email: data.client.email
          } : 'NULL'
        });
        
        const newProject: Project = {
          id: data.id,
          title: data.title,
          description: data.description || '',
          category: data.category,
          clientId: data.client_id, // ✅ Preserve clientId
          client: {
            name: data.client?.name || 'Unknown Client',
            address: data.client?.address || '',
            phone: data.client?.phone,
            email: data.client?.email
          },
          status: data.status,
          createdAt: data.created_at,
          scheduledDate: data.scheduled_date,
          completedDate: data.completed_date,
          timeEntries: data.time_entries || [],
          materials: data.materials || [],
          attachments: data.attachments || [],
          hourlyRate: data.hourly_rate,
          serviceId: data.service_id,
          notes: data.notes,
          assignedMembers: data.assigned_members || [],
          projectManager: data.project_manager,
          priority: data.priority,
          estimatedHours: data.estimated_hours,
          isFixedPrice: data.is_fixed_price,
          fixedPrice: data.fixed_price
        };
        setProjects(prev => [...prev, newProject]);
        return newProject; // Return the created project with its ID
      }
      
      throw new Error('Failed to create project: No data returned');
    } catch (error) {
      console.error('Error in addProject:', error);
      throw error;
    }
  };

  const updateProject = async (updatedProject: Project) => {
    try {
      // Auto-update status based on time entries
      let newStatus = updatedProject.status;
      
      // If project has time entries and is currently 'upcoming', change to 'in-progress'
      if (updatedProject.status === 'upcoming' && updatedProject.timeEntries.length > 0) {
        newStatus = 'in-progress';
      }

      const { error } = await supabase
        .from('projects')
        .update({
          title: updatedProject.title,
          description: updatedProject.description,
          category: updatedProject.category,
          client_id: updatedProject.clientId || null, // ✅ Ensure null for empty clientId
          status: newStatus,
          scheduled_date: updatedProject.scheduledDate || null,
          completed_date: updatedProject.completedDate || null,
          hourly_rate: updatedProject.hourlyRate,
          service_id: updatedProject.serviceId || null, // ✅ Ensure null for empty UUID
          notes: updatedProject.notes || null,
          assigned_members: updatedProject.assignedMembers || [],
          project_manager: updatedProject.projectManager || null, // ✅ Ensure null for empty UUID
          priority: updatedProject.priority || 'medium',
          estimated_hours: updatedProject.estimatedHours || null,
          is_fixed_price: updatedProject.isFixedPrice || false,
          fixed_price: updatedProject.fixedPrice || null,
          time_entries: updatedProject.timeEntries,
          materials: updatedProject.materials,
          attachments: updatedProject.attachments
        })
        .eq('id', updatedProject.id);

      if (error) {
        console.error('Error updating project:', error);
        throw error;
      }

      setProjects(prevProjects => 
        prevProjects.map(project => 
          project.id === updatedProject.id ? { ...updatedProject, status: newStatus } : project
        )
      );
    } catch (error) {
      console.error('Error in updateProject:', error);
      throw error;
    }
  };

  const deleteProject = async (id: string) => {
    try {
      const { error } = await supabase
        .from('projects')
        .delete()
        .eq('id', id);

      if (error) {
        console.error('Error deleting project:', error);
        throw error;
      }

      setProjects(prevProjects => prevProjects.filter(project => project.id !== id));
    } catch (error) {
      console.error('Error in deleteProject:', error);
      throw error;
    }
  };

  const getProjectById = (id: string) => {
    return projects.find(project => project.id === id);
  };

  const addTimeEntry = (projectId: string, timeEntry: Omit<TimeEntry, 'id'>) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    const newTimeEntry = { ...timeEntry, id: Date.now().toString() };
    const updatedTimeEntries = [...project.timeEntries, newTimeEntry];
    
    // Auto-update status to 'in-progress' when first time entry is added
    let newStatus = project.status;
    if (project.status === 'upcoming' && project.timeEntries.length === 0) {
      newStatus = 'in-progress';
    }

    const updatedProject = {
      ...project,
      timeEntries: updatedTimeEntries,
      status: newStatus
    };

    updateProject(updatedProject);
  };

  const updateTimeEntry = (projectId: string, timeEntry: TimeEntry) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    const updatedTimeEntries = project.timeEntries.map(entry => 
      entry.id === timeEntry.id ? timeEntry : entry
    );
    
    // Auto-update status to 'in-progress' when time entry is updated (if still upcoming)
    let newStatus = project.status;
    if (project.status === 'upcoming' && project.timeEntries.length > 0) {
      newStatus = 'in-progress';
    }

    const updatedProject = {
      ...project,
      timeEntries: updatedTimeEntries,
      status: newStatus
    };

    updateProject(updatedProject);
  };

  const deleteTimeEntry = (projectId: string, timeEntryId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    const updatedTimeEntries = project.timeEntries.filter(entry => entry.id !== timeEntryId);
    
    // If all time entries are removed and project is 'in-progress', revert to 'upcoming'
    let newStatus = project.status;
    if (project.status === 'in-progress' && updatedTimeEntries.length === 0) {
      newStatus = 'upcoming';
    }

    const updatedProject = {
      ...project,
      timeEntries: updatedTimeEntries,
      status: newStatus
    };

    updateProject(updatedProject);
  };

  const addMaterialToProject = (projectId: string, material: Material, updateInventory?: (id: string, quantity: number) => void) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    // Update inventory if function is provided
    if (updateInventory && material.inventoryId) {
      updateInventory(material.inventoryId, -material.quantity);
    }
    
    const updatedProject = {
      ...project,
      materials: [...project.materials, material]
    };

    updateProject(updatedProject);
  };

  const updateMaterialInProject = (projectId: string, material: Material) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    const updatedProject = {
      ...project,
      materials: project.materials.map(m => 
        m.id === material.id ? material : m
      )
    };

    updateProject(updatedProject);
  };

  const removeMaterialFromProject = (projectId: string, materialId: string, updateInventory?: (id: string, quantity: number) => void) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    // Find the material being removed to restore inventory
    const materialToRemove = project.materials.find(m => m.id === materialId);
    
    if (updateInventory && materialToRemove && materialToRemove.inventoryId) {
      updateInventory(materialToRemove.inventoryId, materialToRemove.quantity);
    }
    
    const updatedProject = {
      ...project,
      materials: project.materials.filter(m => m.id !== materialId)
    };

    updateProject(updatedProject);
  };

  const addAttachmentToProject = (projectId: string, attachment: Omit<ProjectAttachment, 'id' | 'uploadedAt'>) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    const newAttachment: ProjectAttachment = {
      ...attachment,
      id: Date.now().toString(),
      uploadedAt: new Date().toISOString()
    };
    
    const updatedProject = {
      ...project,
      attachments: [...project.attachments, newAttachment]
    };

    updateProject(updatedProject);
  };

  const removeAttachmentFromProject = (projectId: string, attachmentId: string) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    // Clean up blob URL to prevent memory leaks
    const attachmentToRemove = project.attachments.find(a => a.id === attachmentId);
    if (attachmentToRemove && attachmentToRemove.url.startsWith('blob:')) {
      URL.revokeObjectURL(attachmentToRemove.url);
    }
    
    const updatedProject = {
      ...project,
      attachments: project.attachments.filter(a => a.id !== attachmentId)
    };

    updateProject(updatedProject);
  };

  return (
    <ProjectsContext.Provider value={{
      projects,
      addProject,
      updateProject,
      deleteProject,
      getProjectById,
      addTimeEntry,
      updateTimeEntry,
      deleteTimeEntry,
      addMaterialToProject,
      removeMaterialFromProject,
      updateMaterialInProject,
      addAttachmentToProject,
      removeAttachmentFromProject
    }}>
      {children}
    </ProjectsContext.Provider>
  );
};